import type {
	TeamworkGraphPersonRelationship,
	TeamworkGraphPersonRelationshipDirection,
} from '@confluence/twg-graph-explorer/entry-points/teamworkGraphModel.ts';

import type { BylineContentType } from './contentAri.ts';

export type PagePeopleRelationshipDefinition = {
	cypherName: string;
	relationship: TeamworkGraphPersonRelationship;
	direction: TeamworkGraphPersonRelationshipDirection;
};

/**
 * Local snapshot of the Teamwork Graph registry entries that connect a Confluence page and an
 * Atlassian user. Confluence cannot import the Platform registry without coupling product bundles.
 */
export const PAGE_PEOPLE_RELATIONSHIPS: readonly PagePeopleRelationshipDefinition[] = [
	{
		cypherName: 'atlassian_user_created_confluence_page',
		relationship: 'created',
		direction: 'incoming',
	},
	{
		cypherName: 'atlassian_user_contributed_to_confluence_page',
		relationship: 'contributed',
		direction: 'incoming',
	},
	{
		cypherName: 'atlassian_user_owns_confluence_page',
		relationship: 'owns',
		direction: 'incoming',
	},
	{
		cypherName: 'atlassian_user_updated_confluence_page',
		relationship: 'updated',
		direction: 'incoming',
	},
	{
		cypherName: 'atlassian_user_viewed_confluence_page',
		relationship: 'viewed',
		direction: 'incoming',
	},
	{
		cypherName: 'atlassian_user_favorited_confluence_page',
		relationship: 'favorited',
		direction: 'incoming',
	},
	{
		cypherName: 'atlassian_user_liked_confluence_page',
		relationship: 'liked',
		direction: 'incoming',
	},
	{
		cypherName: 'atlassian_user_mentioned_in_confluence_page',
		relationship: 'mentioned-in',
		direction: 'incoming',
	},
	{
		cypherName: 'atlassian_user_snapshotted_confluence_page',
		relationship: 'snapshotted',
		direction: 'incoming',
	},
	{
		cypherName: 'atlassian_user_watches_confluence_page',
		relationship: 'watches',
		direction: 'incoming',
	},
	{
		cypherName: 'confluence_page_shared_with_atlassian_user',
		relationship: 'shared-with',
		direction: 'outgoing',
	},
];

/**
 * The registry entries that connect a Confluence blog post and an Atlassian user. The registry
 * names these edges separately from the page ones and defines no owns, liked, mentioned-in, or
 * snapshotted edge for blog posts.
 */
const BLOGPOST_PEOPLE_RELATIONSHIPS: readonly PagePeopleRelationshipDefinition[] = [
	{
		cypherName: 'atlassian_user_created_confluence_blogpost',
		relationship: 'created',
		direction: 'incoming',
	},
	{
		cypherName: 'atlassian_user_contributed_to_confluence_blogpost',
		relationship: 'contributed',
		direction: 'incoming',
	},
	{
		cypherName: 'atlassian_user_updated_confluence_blogpost',
		relationship: 'updated',
		direction: 'incoming',
	},
	{
		cypherName: 'atlassian_user_viewed_confluence_blogpost',
		relationship: 'viewed',
		direction: 'incoming',
	},
	{
		cypherName: 'atlassian_user_favorited_confluence_blogpost',
		relationship: 'favorited',
		direction: 'incoming',
	},
	{
		cypherName: 'atlassian_user_watches_confluence_blogpost',
		relationship: 'watches',
		direction: 'incoming',
	},
	{
		cypherName: 'confluence_blogpost_shared_with_atlassian_user',
		relationship: 'shared-with',
		direction: 'outgoing',
	},
];

/** The registry entries that connect the given content type and an Atlassian user. */
export const getPagePeopleRelationships = (
	contentType: BylineContentType | undefined,
): readonly PagePeopleRelationshipDefinition[] =>
	contentType === 'blogpost' ? BLOGPOST_PEOPLE_RELATIONSHIPS : PAGE_PEOPLE_RELATIONSHIPS;

export const isPagePeopleRelationship = (
	value: string | undefined,
): value is TeamworkGraphPersonRelationship =>
	PAGE_PEOPLE_RELATIONSHIPS.some(({ relationship }) => relationship === value);

export const isPagePeopleRelationshipDirection = (
	value: string | undefined,
): value is TeamworkGraphPersonRelationshipDirection =>
	value === 'incoming' || value === 'outgoing';
